import { useState, useMemo, useEffect, useCallback } from 'react'

import { AutocompleteMultiselection } from '@yes.technology/react-toolkit'

import useActions from 'modules/interaction/hooks/useActions'
import useSuggestionsFromSources from 'modules/interaction/hooks/useSuggestionsFromSources'

import ActivityWithActions from '../ActivityWithActions/ActivityWithActions'

import useMultiselectionOnSelect from './useMultiselectionOnSelect'

import { MULTISELECTION_SEPARATOR } from 'utils/constants'
import useDataSourcesFromFilteraction from 'modules/interaction/hooks/useDataSourcesFromFilteraction'

const getOptions = ({ selectedOptions, suggestions }) => {
  const mappedSuggestions = suggestions.map(({ uuid, des }) => ({
    id: uuid,
    value: des
  }))

  const uniqueSelectedOptions = selectedOptions.filter(
    (selectedOption) =>
      !suggestions.find((suggestion) => suggestion.uuid === selectedOption.id)
  )

  const options = [...uniqueSelectedOptions, ...mappedSuggestions]

  return options
}

const getSelectedOptions = (value) => {
  if (!value || typeof value.id !== 'string') {
    return []
  }

  const uuids = value.id.split(MULTISELECTION_SEPARATOR)
  const descriptions = value.des.split(MULTISELECTION_SEPARATOR)

  const selectedOptions = uuids.map((uuid, index) => ({
    id: uuid,
    value: descriptions[index]
  }))

  return selectedOptions
}

const optionsToObjects = (options) => {
  if (!options || !Array.isArray(options)) {
    return []
  }
  return options.map((option) => ({
    uuid: option.id,
    des: option.value
  }))
}

const EMPTY_ARRAY = []

const ActivityAutocompleteMultiselection = ({
  parentPath,
  filteraction,
  filteractionParams,
  value,
  onChange,
  endpoint,
  dataSources: manifestDataSources = EMPTY_ARRAY,
  statusClass,
  isFetchingBlueprints = false,
  actions,
  disabled,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState('')

  const selectedOptions = useMemo(() => getSelectedOptions(value), [value])

  const dataSources = useDataSourcesFromFilteraction({
    filteraction,
    filteractionParams,
    manifestDataSources,
    endpoint
  })

  const onSourceValuesChange = useCallback(() => {
    if (selectedOptions.length) {
      onChange({ id: '', des: '' })
    }
  }, [selectedOptions, onChange])

  const {
    suggestions,
    debouncedFetchSuggestions,
    resetSuggestions,
    isLoading: isLoadingSuggestions,
    hasAllSourceValues
  } = useSuggestionsFromSources({
    parentPath,
    filteraction,
    filteractionParams,
    endpoint,
    dataSources,
    onSourceValuesChange
  })

  const options = useMemo(
    () => getOptions({ selectedOptions, suggestions }),
    [selectedOptions, suggestions]
  )

  const onSelect = useMultiselectionOnSelect({ selectedOptions, onChange })

  useEffect(() => {
    if (filteractionParams?.length && hasAllSourceValues) {
      const search = inputValue === '' ? '.*' : inputValue
      debouncedFetchSuggestions(search)
    }
  }, [
    hasAllSourceValues,
    inputValue,
    filteractionParams,
    debouncedFetchSuggestions
  ])

  const handleOnChange = useCallback(
    (value) => {
      if (value === '') {
        resetSuggestions()
      } else {
        debouncedFetchSuggestions(value)
      }

      setInputValue(value)
    },
    [debouncedFetchSuggestions, resetSuggestions, setInputValue]
  )

  const selectedObjects = useMemo(
    () => optionsToObjects(selectedOptions),
    [selectedOptions]
  )

  const { onClickAction, isLoadingFilteraction } = useActions({
    actions,
    filteraction,
    onSelectAndLink: (values) => {
      onSelect(
        values.map((value) => ({
          id: value.uuid,
          value: value.des
        })),
        'set'
      )
    },
    selectedObjects
  })

  const isLoading = isLoadingFilteraction || isLoadingSuggestions

  return (
    <ActivityWithActions {...{ actions, onClickAction }}>
      <AutocompleteMultiselection
        {...rest}
        statusClass={isLoading ? 'processing' : statusClass}
        onChange={handleOnChange}
        value={inputValue}
        isLoadingBlueprints={isFetchingBlueprints}
        disabled={disabled || !hasAllSourceValues}
        autoComplete='new-password'
        {...{ options, selectedOptions, onSelect }}
      />
    </ActivityWithActions>
  )
}

export default ActivityAutocompleteMultiselection
